import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { EmptyFieldType } from '../adv2-config';

export interface IEmptyFieldEntry {
  key: string;
  value: string;
}

export interface IEmptyFieldsPayload {
  byType: IEmptyFieldEntry[];
  byIdentifier: IEmptyFieldEntry[];
}

/**
 * Builds a single blank-field-default control group. The `type` control holds the
 * EmptyFieldType *key* (e.g. 'BY_TYPE'), not its label.
 */
export function createEmptyFieldGroup(type = '', key = '', value = ''): UntypedFormGroup {
  return new UntypedFormGroup({
    type: new UntypedFormControl(type, Validators.required),
    key: new UntypedFormControl(key, Validators.required),
    value: new UntypedFormControl(value),
  });
}

/**
 * Converts the emptyFields object held on the server config into control groups,
 * ready to be pushed onto the form array.
 */
export function emptyFieldsToControls(emptyFields: Partial<IEmptyFieldsPayload>): UntypedFormGroup[] {
  const groups: UntypedFormGroup[] = [];

  if (emptyFields?.byIdentifier) {
    groups.push(...entriesToControls(emptyFields.byIdentifier, EmptyFieldType.BY_IDENTIFIER));
  }
  if (emptyFields?.byType) {
    groups.push(...entriesToControls(emptyFields.byType, EmptyFieldType.BY_TYPE));
  }

  return groups;
}

export function entriesToControls(values: IEmptyFieldEntry[], emptyFieldType: EmptyFieldType): UntypedFormGroup[] {
  const typeKey = emptyFieldTypeKey(emptyFieldType);
  return (values ?? []).map((entry) => createEmptyFieldGroup(typeKey, entry.key, entry.value));
}

/**
 * Converts the form array back into the emptyFields shape the server expects.
 */
export function controlsToEmptyFields(array: UntypedFormArray): IEmptyFieldsPayload {
  return {
    byType: pickEmptyFieldsOfType(array, EmptyFieldType.BY_TYPE),
    byIdentifier: pickEmptyFieldsOfType(array, EmptyFieldType.BY_IDENTIFIER),
  };
}

export function pickEmptyFieldsOfType(array: UntypedFormArray, emptyFieldType: EmptyFieldType): IEmptyFieldEntry[] {
  return array.controls
    .filter((control: AbstractControl) => {
      const typeKey = control.get('type').value as keyof typeof EmptyFieldType;
      return EmptyFieldType[typeKey] === emptyFieldType;
    })
    .map((control: AbstractControl) => ({
      key: control.get('key').value,
      value: control.get('value').value,
    }));
}

/**
 * Resolves an EmptyFieldType label back to its enum key, which is what the
 * `type` control stores.
 */
function emptyFieldTypeKey(emptyFieldType: EmptyFieldType): string {
  const index = Object.values(EmptyFieldType).indexOf(emptyFieldType);
  return Object.keys(EmptyFieldType)[index];
}
